<script setup lang="ts">
import { useAppMode } from '@/composables/useAppMode'
import ChipsCount from '@/components/shared/ChipsCount.vue'

const { mode } = useAppMode()

withDefaults(
  defineProps<{
    smallBlind?: number
    value: number | string | null
    variant?: 'light' | 'dark'
    playerId?: number | string
    layout?: 'horizontal' | 'vertical' | 'hirizontal-reverse'
  }>(),
  {
    smallBlind: 10,
    variant: 'dark',
    playerId: 'no',
    layout: 'horizontal'
  }
)
</script>

<template>
  <div
    class="chip-stack"
    :class="`chip-stack--${variant} chip-stack--${layout} chip-stack--${mode}`"
  >
    <transition appear name="chip-stack" mode="out-in">
      <div :key="`${playerId}-${value ?? 'null'}`" class="chip-stack__stack">
        <div class="chip-stack__stack-chip" />
        <div v-if="Number(value) >= smallBlind * 2" class="chip-stack__stack-chip" />
        <div v-if="Number(value) > smallBlind * 2" class="chip-stack__stack-chip" />
      </div>
    </transition>
    <div class="chip-stack__value" :class="`chip-stack__value--${mode}`">
      <chips-count :value="Number(value)" />
    </div>
  </div>
</template>

<style lang="scss">
.chip-stack {
  display: flex;
  font-size: 14px;
  align-items: flex-end;
  justify-content: center;

  &--horizontal {
    flex-direction: row;
    gap: calc(var(--tw) * 0.35);
  }

  &--hirizontal-reverse {
    display: flex;
    flex-direction: row-reverse;
    white-space: nowrap;
  }

  &--vertical {
    flex-direction: column-reverse;
    align-items: center;
    gap: calc(var(--th) * 1.8);

    &.chip-stack--desktop {
      .chip-stack__stack {
        top: 1px;
      }
    }
  }

  &--dark {
    .chip-stack__value {
      padding: 0 6px;
      height: 18px;
      font-size: 15px;
      line-height: 18px;
      font-weight: 500;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.35);
      backdrop-filter: blur(4px);
      color: #e4e4e4;
    }
  }

  &--desktop {
    .chip-stack__stack {
      top: -2px;
      right: 2px;
    }
  }

  &__stack {
    position: relative;
    width: var(--table-chip-width);
    height: calc(var(--table-chip-height) * 0.25);
  }

  &__stack-chip {
    transition: all 0.15s ease-in-out;
    background-image: url('@/assets/chip.png');
    filter: drop-shadow(3px 3px 3px rgba(0, 0, 0, 0.25));
    background-size: cover;
    width: var(--table-chip-width);
    height: var(--table-chip-height);
    position: absolute;
  }

  &__value {
    padding: 0 calc(var(--th) * 0.3);
    color: #e4e4e4;
    min-width: 55px;
    display: flex;
    justify-content: center;
    align-items: center;

    &--mobile {
      height: var(--table-chip-height);
    }
  }
}

.chip-stack-enter-active {
  transition: all 0.5s ease-in-out;
}

.chip-stack-enter-from {
  opacity: 0.9;

  .chip-stack__stack-chip {
    transform: translateY(-60%);
    opacity: 0.9;
  }
}

.table__pots-leave-active {
  .chip-stack__stack-chip {
    &:nth-child(1) {
      bottom: 0;
      transition-delay: 500ms;
    }

    &:nth-child(2) {
      bottom: 50%;
      transition-delay: 200ms;
    }

    &:nth-child(3) {
      bottom: 100%;
      transition-delay: 0ms;
    }
  }
}
</style>
